import React, { useState } from 'react';
import { lineDiff } from '../../utils/lineDiff';
import '../PartnerSyncTemplates.css';

// Run a custom code snippet against a pasted payload and show what it changed.
//
// SHARED by every custom code editor — sync template snippets (PartnerSyncTemplates) and integration route
// snippets (IntegrationRouteCodePage) — because they share the execution contract: the body is compiled with
// new Function(payload, context) and the appended call falls back to the payload when the snippet defines
// no transform(). A snippet that works here works where it runs.
//
// CLIENT-SIDE, deliberately: the lambdas hold database credentials, and evaluating pasted JavaScript inside
// one would be a far larger risk than running it in the author's own tab.
//
// The snippet MUTATES the payload in place, so `before` is snapshotted first — diffing against the object it
// just edited would always show no change.
//
// Props:
//   code             the snippet
//   context          what the runtime passes as `context`
//   payload          the pasted JSON (controlled, so an assistant beside this can quote it)
//   onPayloadChange  (text) => void
//   placeholder      what to paste
//   failureMeaning   what a throw does where the snippet really runs
export const runSnippet = (code, payloadText, context) => {
    const text = (payloadText || '').trim();
    if (!text) return { error: 'Paste a payload to test against.' };
    let input;
    try {
        input = JSON.parse(text);
    } catch (e) {
        return { error: 'Payload is not valid JSON: ' + e.message };
    }
    const before = JSON.parse(JSON.stringify(input));
    try {
        // eslint-disable-next-line no-new-func
        const fn = new Function('payload', 'context',
            `${code}\n;return (typeof transform === 'function') ? transform(payload, context) : payload;`);
        const returned = fn(input, context);
        const after = returned === undefined ? input : returned;
        return { before, after, changed: JSON.stringify(before) !== JSON.stringify(after) };
    } catch (e) {
        return { thrown: e && e.message ? e.message : String(e) };
    }
};

const CustomCodeTester = ({ code, context, payload, onPayloadChange, placeholder, failureMeaning }) => {
    const [result, setResult] = useState(null);

    const test = () => {
        const r = runSnippet(code, payload, context);
        // A snippet that throws fails the record where it really runs — shown as a failure, not a no-op.
        setResult(r.thrown ? { error: `Snippet threw: ${r.thrown} — ${failureMeaning}` } : r);
    };

    return (
        <div className="pst-test-body">
            <label htmlFor="custom-code-test-payload">PAYLOAD (JSON)</label>
            <textarea
                id="custom-code-test-payload"
                className="pst-test-input"
                value={payload}
                onChange={e => { onPayloadChange(e.target.value); setResult(null); }}
                spellCheck={false}
                rows={8}
                placeholder={placeholder}
            />
            <div className="pst-test-actions">
                <button type="button" className="btn btn-secondary btn-sm" onClick={test} disabled={!code}>
                    Test
                </button>
                <span className="pst-test-note">Runs in your browser against the code above. Nothing is saved or sent.</span>
            </div>

            {result && result.error && <div className="pst-test-error">{result.error}</div>}
            {result && !result.error && !result.changed && (
                <div className="pst-test-nochange">
                    The snippet ran without error and changed nothing — this payload passes through untouched.
                </div>
            )}
            {result && !result.error && result.changed && (
                <>
                    <div className="pst-test-legend">
                        <span className="pst-diff-del">- removed by the snippet</span>
                        <span className="pst-diff-add">+ added by the snippet</span>
                    </div>
                    <pre className="pst-test-diff">
                        {lineDiff(JSON.stringify(result.before, null, 2), JSON.stringify(result.after, null, 2)).map((l, i) => (
                            <div key={i} className={l.t === 'add' ? 'pst-diff-add' : l.t === 'del' ? 'pst-diff-del' : 'pst-diff-same'}>
                                <span className="pst-diff-gutter">{l.t === 'add' ? '+' : l.t === 'del' ? '-' : ' '}</span>{l.s}
                            </div>
                        ))}
                    </pre>
                </>
            )}
        </div>
    );
};

export default CustomCodeTester;
